import { useCallback, useState } from 'react';

/** Identifies one top-up request; only the current session's token can settle into a lock. */
export interface TopUpToken {
    readonly dealerId: string;
}

export interface TakingDealerLock {
    lockedDealerId: string | null;
    /** The dealer of a top-up request still in flight; the picker and Submit wait for it. */
    pendingDealerId: string | null;
    beginTopUp: (dealerId: string) => TopUpToken;
    /** Null for a failed request: clears pending without locking. */
    settle: (token: TopUpToken, recordedDealerId: string | null) => void;
    release: () => void;
}

interface Held {
    agreementId: string | undefined;
    dealerId: string | null;
    pending: TopUpToken | null;
}

const released = (agreementId: string | undefined): Held => ({ agreementId, dealerId: null, pending: null });

/** Release (invoice submitted, Back, agreement change) drops the pending token, so a top-up
 *  begun before it settles inertly; a draft reset or tab remount does not touch this state. */
export function useTakingDealerLock(agreementId: string | undefined): TakingDealerLock {
    const [held, setHeld] = useState<Held>(() => released(agreementId));

    let current = held;
    if (held.agreementId !== agreementId) {
        current = released(agreementId);
        setHeld(current);
    }

    const beginTopUp = useCallback((dealerId: string) => {
        const token: TopUpToken = { dealerId };
        setHeld((prev) => ({ ...prev, pending: token }));
        return token;
    }, []);

    const settle = useCallback((token: TopUpToken, recordedDealerId: string | null) => {
        setHeld((prev) => {
            if (prev.pending !== token) return prev;
            return { ...prev, dealerId: recordedDealerId ?? prev.dealerId, pending: null };
        });
    }, []);

    const release = useCallback(() => setHeld(released(agreementId)), [agreementId]);

    return {
        lockedDealerId: current.dealerId,
        pendingDealerId: current.pending?.dealerId ?? null,
        beginTopUp,
        settle,
        release,
    };
}
